export const SOUTH_AFRICAN_TAX_PIN_ERROR =
  'Invalid SARS Tax PIN. It must be exactly 10 alphanumeric characters.';
export const SOUTH_AFRICAN_ID_NUMBER_ERROR = 'Invalid South African ID number.';
export const SOUTH_AFRICAN_ID_DATE_ERROR = 'Invalid date of birth in ID number.';
export const SOUTH_AFRICAN_ID_CHECKSUM_ERROR = 'Invalid South African ID number checksum.';

export const SOUTH_AFRICAN_TAX_PIN_REGEX = /^[A-Z0-9]{10}$/;
export const SOUTH_AFRICAN_ID_NUMBER_REGEX = /^[0-9]{13}$/;

export const normalizeTaxPinOrIdNumber = (value?: string): string => (value ?? '').trim();
export const normalizeSarsTaxPin = (value?: string): string => normalizeTaxPinOrIdNumber(value).toUpperCase();

export const isValidSarsTaxPin = (value?: string): boolean => {
  const normalized = normalizeSarsTaxPin(value);
  return SOUTH_AFRICAN_TAX_PIN_REGEX.test(normalized);
};

const getFullYearFromShortYear = (yy: number, now: Date = new Date()): number => {
  const currentYearShort = now.getFullYear() % 100;
  return yy <= currentYearShort ? 2000 + yy : 1900 + yy;
};

export const isValidSouthAfricanIdDate = (idNumber: string, now: Date = new Date()): boolean => {
  if (!SOUTH_AFRICAN_ID_NUMBER_REGEX.test(idNumber)) {
    return false;
  }

  const yy = Number(idNumber.slice(0, 2));
  const mm = Number(idNumber.slice(2, 4));
  const dd = Number(idNumber.slice(4, 6));

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
    return false;
  }

  const fullYear = getFullYearFromShortYear(yy, now);
  const date = new Date(Date.UTC(fullYear, mm - 1, dd));

  return (
    date.getUTCFullYear() === fullYear &&
    date.getUTCMonth() === mm - 1 &&
    date.getUTCDate() === dd
  );
};

export const getSouthAfricanIdDateOfBirth = (value?: string, now: Date = new Date()): string | undefined => {
  const idNumber = normalizeTaxPinOrIdNumber(value);
  if (!isValidSouthAfricanIdDate(idNumber, now)) return undefined;

  const fullYear = getFullYearFromShortYear(Number(idNumber.slice(0, 2)), now);
  return `${fullYear}-${idNumber.slice(2, 4)}-${idNumber.slice(4, 6)}`;
};

// SA ID checksum uses Luhn-style weighting on first 12 digits to match 13th check digit.
export const passesSouthAfricanIdChecksum = (idNumber: string): boolean => {
  if (!SOUTH_AFRICAN_ID_NUMBER_REGEX.test(idNumber)) {
    return false;
  }

  const digits = idNumber.split('').map(Number);
  const checkDigit = digits[12];

  let oddSum = 0;
  for (let i = 0; i < 12; i += 2) {
    oddSum += digits[i];
  }

  let evenConcat = '';
  for (let i = 1; i < 12; i += 2) {
    evenConcat += String(digits[i]);
  }

  const doubled = String(Number(evenConcat) * 2);
  const evenSum = doubled.split('').reduce((sum, d) => sum + Number(d), 0);

  const total = oddSum + evenSum;
  const computedCheckDigit = (10 - (total % 10)) % 10;

  return computedCheckDigit === checkDigit;
};

export const isValidSouthAfricanIdNumber = (value?: string): boolean => {
  const normalized = normalizeTaxPinOrIdNumber(value);

  if (!SOUTH_AFRICAN_ID_NUMBER_REGEX.test(normalized)) {
    return false;
  }

  const citizenshipDigit = normalized[10];
  if (citizenshipDigit !== '0' && citizenshipDigit !== '1') {
    return false;
  }

  if (!isValidSouthAfricanIdDate(normalized)) {
    return false;
  }

  return passesSouthAfricanIdChecksum(normalized);
};

export const validateSarsTaxPin = (value?: string): true | string => {
  const normalized = normalizeSarsTaxPin(value);

  if (!normalized) {
    return 'Tax PIN is required';
  }

  return isValidSarsTaxPin(normalized) ? true : SOUTH_AFRICAN_TAX_PIN_ERROR;
};

export const validateSouthAfricanIdNumber = (value?: string): true | string => {
  const normalized = normalizeTaxPinOrIdNumber(value);

  if (!normalized) {
    return 'ID number is required';
  }

  if (!SOUTH_AFRICAN_ID_NUMBER_REGEX.test(normalized)) {
    return SOUTH_AFRICAN_ID_NUMBER_ERROR;
  }

  if (!isValidSouthAfricanIdDate(normalized)) {
    return SOUTH_AFRICAN_ID_DATE_ERROR;
  }

  const citizenshipDigit = normalized[10];
  if (citizenshipDigit !== '0' && citizenshipDigit !== '1') {
    return SOUTH_AFRICAN_ID_NUMBER_ERROR;
  }

  if (!passesSouthAfricanIdChecksum(normalized)) {
    return SOUTH_AFRICAN_ID_CHECKSUM_ERROR;
  }

  return true;
};
